import DashboardService from "@/services/api/dashboard";
import {
  createAsyncThunk,
  createSlice,
  type PayloadAction,
} from "@reduxjs/toolkit";

// 1️⃣ Define types for the slice state
interface ExpiryAlertProducts {
  count: number;
  rows: any[]; // replace `any` with a proper type if you know the structure
}

interface DashboardData {
  totalCustomer: number;
  totalOrder: number;
  totalSale: number;
  totalPending: number;
  totalVendor: number;
  customerGrowth: number;
  orderGrowth: number;
  salesGrowth: number;
  pendingOrderGrowth: number;
  vendorGrowth: number;
  graphData: any[]; // replace with proper type if known
  expiryAlertProducts: ExpiryAlertProducts;
  topProducts: any[]; // replace with proper type if known
}

interface DashboardState {
  isFetching: boolean;
  data: DashboardData;
}

// 2️⃣ Initial state
const initialState: DashboardState = {
  isFetching: false,
  data: {
    totalCustomer: 0,
    totalOrder: 0,
    totalSale: 0,
    totalPending: 0,
    totalVendor: 0,
    customerGrowth: 0,
    orderGrowth: 0,
    salesGrowth: 0,
    pendingOrderGrowth: 0,
    vendorGrowth: 0,
    graphData: [],
    expiryAlertProducts: {
      count: 0,
      rows: [],
    },
    topProducts: [],
  },
};

// 3️⃣ Async thunks
export const getDashboardData = createAsyncThunk<
  Partial<DashboardData>, // fulfilled payload
  void, // argument to the thunk
  { rejectValue: string }
>("dashboard/getDashboardData", async (_, { rejectWithValue }) => {
  try {
    const response = await DashboardService.getDashboardData();
    return response.data || {};
  } catch (error: any) {
    return rejectWithValue(error.message);
  }
});

export const getDashboardGraphData = createAsyncThunk<
  any[],
  void,
  { rejectValue: string }
>("dashboard/getDashboardGraphData", async (_, { rejectWithValue }) => {
  try {
    const response = await DashboardService.getDashboardGraphData();
    return response.data || [];
  } catch (error: any) {
    return rejectWithValue(error.message);
  }
});

export const getDashboardExpiryAlertData = createAsyncThunk<
  ExpiryAlertProducts,
  void,
  { rejectValue: string }
>("dashboard/getDashboardExpiryAlertData", async (_, { rejectWithValue }) => {
  try {
    const response = await DashboardService.getDashboardExpiryAlertData();
    return response.data || { count: 0, rows: [] };
  } catch (error: any) {
    return rejectWithValue(error.message);
  }
});

export const getDashboardTopProductsData = createAsyncThunk<
  any[],
  void,
  { rejectValue: string }
>("dashboard/getDashboardTopProductsData", async (_, { rejectWithValue }) => {
  try {
    const response = await DashboardService.getDashboardTopProductsData();
    return response.data || [];
  } catch (error: any) {
    return rejectWithValue(error.message);
  }
});

// 4️⃣ Slice
const dashboardSlice = createSlice({
  name: "dashboard",
  initialState,
  reducers: {
    clear: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      // getDashboardData
      .addCase(getDashboardData.pending, (state) => {
        state.isFetching = true;
      })
      .addCase(
        getDashboardData.fulfilled,
        (state, action: PayloadAction<Partial<DashboardData>>) => {
          state.isFetching = false;
          state.data = {
            ...state.data,
            ...action.payload,
          };
        }
      )
      .addCase(getDashboardData.rejected, (state) => {
        state.isFetching = false;
      })

      // getDashboardGraphData
      .addCase(getDashboardGraphData.pending, (state) => {
        state.isFetching = true;
      })
      .addCase(
        getDashboardGraphData.fulfilled,
        (state, action: PayloadAction<any[]>) => {
          state.isFetching = false;
          state.data.graphData = action.payload;
        }
      )
      .addCase(getDashboardGraphData.rejected, (state) => {
        state.isFetching = false;
      })

      // getDashboardExpiryAlertData
      .addCase(getDashboardExpiryAlertData.pending, (state) => {
        state.isFetching = true;
      })
      .addCase(
        getDashboardExpiryAlertData.fulfilled,
        (state, action: PayloadAction<ExpiryAlertProducts>) => {
          state.isFetching = false;
          state.data.expiryAlertProducts = action.payload;
        }
      )
      .addCase(getDashboardExpiryAlertData.rejected, (state) => {
        state.isFetching = false;
      })

      // getDashboardTopProductsData
      .addCase(getDashboardTopProductsData.pending, (state) => {
        state.isFetching = true;
      })
      .addCase(
        getDashboardTopProductsData.fulfilled,
        (state, action: PayloadAction<any[]>) => {
          state.isFetching = false;
          state.data.topProducts = action.payload;
        }
      )
      .addCase(getDashboardTopProductsData.rejected, (state, action) => {
        state.isFetching = false;
        console.error("Failed to fetch top products:", action.payload);
      });
  },
});

export const { clear } = dashboardSlice.actions;
export default dashboardSlice.reducer;
